import { useState } from 'react';
import { useDispatch } from 'react-redux';
import xhr from '../../../../../utils/xhr';
import utils from '../../../../../utils/utils';
import { showNotification } from '../../../../../redux/notification';
import { getShiftsToRequest } from '../../../../../hooks/useShiftsForm';

const buildPostUrlAndPayload = ({ facilityId, shiftsToRequest, isFlex, isHybrid, isOnCall }) => {
	if (isOnCall) {
		return {
			url: `/on-call/facility/${facilityId}/shifts`,
			payload: { facilityId, onCall: true, shiftsData: shiftsToRequest }
		};
	}
	if (isHybrid) {
		return { url: `/hybrid/facility/${facilityId}/shifts`, payload: shiftsToRequest };
	}
	if (isFlex) {
		return { url: `/flex/facility/${facilityId}/shifts`, payload: shiftsToRequest };
	}
	return { url: '/shifts', payload: { facilityId, shiftsData: shiftsToRequest } };
};

const useRequestShiftsSubmit = ({
	selectedFacility,
	shiftsGroups,
	isFlex,
	isHybrid,
	isOnCall,
	needMoreShifts,
	reset,
	handleBackendErrors,
	checkIntegrity,
	setIsLoading,
	updateFeed,
	onClose,
	confirmLabel = 'Request shifts'
}) => {
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showFlexStructuresAlreadyStartedModal, setShowFlexStructuresAlreadyStartedModal] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const dispatch = useDispatch();

	const closeAllConfirmations = () => {
		setShowConfirmationModal(false);
		setShowFlexStructuresAlreadyStartedModal(false);
	};

	const submit = () => {
		const verdict = checkIntegrity(isFlex);
		if (!verdict.isValid) return;
		if (verdict.hasFlexStructuresAlreadyStarted) {
			setShowFlexStructuresAlreadyStartedModal(true);
		} else {
			setShowConfirmationModal(true);
		}
	};

	const postShifts = async () => {
		if (!selectedFacility) return;
		const facilityId = selectedFacility.id;
		setIsSubmitting(true);
		setIsLoading(true);
		try {
			const shiftsToRequest = getShiftsToRequest(shiftsGroups, isFlex);
			const { url, payload } = buildPostUrlAndPayload({
				facilityId,
				shiftsToRequest,
				isFlex,
				isHybrid,
				isOnCall
			});
			const shiftsPosted = await xhr.request('POST', url, payload);
			if (shiftsPosted) {
				dispatch(showNotification('success', 'Shifts requested successfully'));
				if (updateFeed) updateFeed();
				if (needMoreShifts) {
					reset();
				} else {
					onClose();
				}
			}
		} catch (e) {
			if (e.response && e.response.status === 403) {
				dispatch(
					showNotification(
						'error',
						'Shift could not be added as you do not have permission to add shifts at this facility.'
					)
				);
				onClose();
			} else {
				const errorMessage = utils.handleNetworkErrorMessage(e, 'Failed to save requested shifts. Please try again.');
				if (e.response && e.response.data) {
					handleBackendErrors(e.response.data.errors);
				}
				dispatch(showNotification('error', errorMessage));
			}
		} finally {
			closeAllConfirmations();
			setIsSubmitting(false);
			setIsLoading(false);
		}
	};

	const displayFlexStartedConfirmation = () => {
		setShowFlexStructuresAlreadyStartedModal(false);
		setShowConfirmationModal(true);
	};

	let variant;
	if (showFlexStructuresAlreadyStartedModal) {
		variant = 'flex-already-started';
	} else if (isFlex) {
		variant = 'flex';
	} else {
		variant = 'non-flex';
	}

	return {
		submit,
		confirmLabel,
		submitDisabled: false,
		isSubmitting,
		confirmation: {
			isOpen: showConfirmationModal || showFlexStructuresAlreadyStartedModal,
			variant,
			onConfirm: showFlexStructuresAlreadyStartedModal ? displayFlexStartedConfirmation : postShifts,
			onCancel: closeAllConfirmations
		}
	};
};

export default useRequestShiftsSubmit;
